import { ref, onMounted } from "vue"

export type DevicePlatform = "ios" | "android" | "other"

/**
 * Detects the visitor's mobile platform so store links can be narrowed to the
 * one that is actually installable on their device.
 *
 * Detection is client-only and deliberately so. Branching on the server's
 * User-Agent would make the rendered HTML vary per device, which breaks any
 * shared/CDN caching in front of Nitro unless `Vary: User-Agent` is set. The
 * value therefore starts as "other" (show every store) and narrows after mount,
 * matching the SSR-safe pattern already used in components/footer/quickLinks.vue.
 */
const detect = (): DevicePlatform => {
  if (typeof navigator === "undefined") return "other"

  const ua = navigator.userAgent || ""

  // User-Agent Client Hints, where supported, is more reliable than sniffing.
  const uaPlatform = (navigator as any).userAgentData?.platform
  if (uaPlatform && String(uaPlatform).toLowerCase() === "android") return "android"

  if (/android/i.test(ua)) return "android"
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios"

  // iPadOS 13+ reports a desktop Macintosh UA. Real Macs report maxTouchPoints 0,
  // so touch support is what separates an iPad from a MacBook here.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return "ios"

  return "other"
}

export const useDevicePlatform = () => {
  const platform = ref<DevicePlatform>("other")

  onMounted(() => {
    platform.value = detect()
  })

  return { platform }
}
